import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Form, Input, Button, message, Upload, List, Space, Popconfirm, Alert, Card, Badge, Divider, Radio, Typography } from 'antd';
import { SettingOutlined, SaveOutlined, DeleteOutlined, UploadOutlined, FileOutlined, InboxOutlined, BarChartOutlined, KeyOutlined, DatabaseOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext.jsx';
import apiService from '../services/apiService';
import DocumentUpload from './DocumentUpload.jsx';
import DocumentList from './DocumentList.jsx';
import ScorecardManager from './ScorecardManager.jsx';

const { TextArea } = Input;
const { Dragger } = Upload;
const { Text } = Typography;

const GlobalSettingsModal = ({ 
  visible, 
  onClose 
}) => {
  const [form] = Form.useForm();
  const [openaiForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('openai');
  
  // OpenAI settings state
  const [openaiSettings, setOpenaiSettings] = useState(null);
  
  // Instructions state - separated by type
  const [chatInstructions, setChatInstructions] = useState(null);
  const [uploadInstructions, setUploadInstructions] = useState(null);
  const [activeInstructionType, setActiveInstructionType] = useState('chat');
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [documentRefresh, setDocumentRefresh] = useState(0);
  
  // Scorecards state
  const [scorecards, setScorecards] = useState([]);
  
  const { getAuthToken } = useAuth();

  useEffect(() => {
    if (visible) {
      loadOpenaiSettings();
      loadInstructions();
      loadScorecards();
    }
  }, [visible]);

  useEffect(() => {
    updateFormValues();
  }, [activeInstructionType, chatInstructions, uploadInstructions]);

  const loadOpenaiSettings = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await apiService.getUserOpenaiSettings(token);
      setOpenaiSettings(response.data || null);
      
      // Set form values
      openaiForm.setFieldsValue({
        storage_type: response.data?.storage_type || 'openai_direct',
        api_key: response.data?.api_key || '',
        vector_storage_id: response.data?.vector_storage_id || '',
        chat_prompt_id: response.data?.chat_prompt_id || '',
        upload_prompt_id: response.data?.upload_prompt_id || '',
        apps_per_chunk: response.data?.apps_per_chunk || 10
      });
    } catch (error) {
      console.log('No OpenAI settings found or error loading:', error);
      setOpenaiSettings(null);
      openaiForm.setFieldsValue({
        storage_type: 'openai_direct',
        api_key: '',
        vector_storage_id: '',
        chat_prompt_id: '',
        upload_prompt_id: '',
        apps_per_chunk: 10
      });
    } finally {
      setLoading(false);
    }
  };

  const saveOpenaiSettings = async (values) => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      await apiService.saveUserOpenaiSettings({
        storage_type: values.storage_type,
        api_key: values.api_key,
        vector_storage_id: values.vector_storage_id,
        chat_prompt_id: values.chat_prompt_id,
        upload_prompt_id: values.upload_prompt_id,
        apps_per_chunk: parseInt(values.apps_per_chunk) || 10
      }, token);
      
      message.success('OpenAI settings saved successfully!');
      await loadOpenaiSettings();
    } catch (error) {
      console.error('Failed to save OpenAI settings:', error);
      message.error('Failed to save OpenAI settings');
    } finally {
      setLoading(false);
    }
  };

  const loadInstructions = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      // Load chat instructions
      try {
        const chatResponse = await apiService.getUserInstructions(token, null, 'chat');
        setChatInstructions(chatResponse.instructions);
      } catch (error) {
        console.log('No chat instructions found');
        setChatInstructions(null);
      }
      
      // Load upload instructions
      try {
        const uploadResponse = await apiService.getUserInstructions(token, null, 'upload');
        setUploadInstructions(uploadResponse.instructions);
      } catch (error) {
        console.log('No upload instructions found');
        setUploadInstructions(null);
      }
      
      // Set form values based on active type
      updateFormValues();
    } catch (error) {
      console.error('Failed to load instructions:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFormValues = () => {
    const instructions = activeInstructionType === 'chat' ? chatInstructions : uploadInstructions;
    form.setFieldsValue({
      instructions: instructions?.instructions || ''
    });
  };



  const saveInstructions = async (values) => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      await apiService.saveUserInstructions({
        instructions: values.instructions,
        type: activeInstructionType,
        // Don't include project_id for global instructions
      }, token);
      
      message.success(`Global ${activeInstructionType} instructions saved successfully!`);
      await loadInstructions();
    } catch (error) {
      console.error('Failed to save instructions:', error);
      message.error('Failed to save instructions');
    } finally {
      setLoading(false);
    }
  };

  const deleteInstructions = async (instructionId) => {
    try {
      const token = getAuthToken();
      await apiService.deleteUserInstructions(instructionId, token);
      message.success('Instructions deleted successfully!');
      await loadInstructions();
    } catch (error) {
      console.error('Failed to delete instructions:', error);
      message.error('Failed to delete instructions');
    }
  };

  const handleDocumentUploadSuccess = (response) => {
    setDocumentRefresh(prev => prev + 1);
  };

  const handleDocumentUploadError = (error) => {
    console.error('Document upload error:', error);
  };

  const handleDocumentDeleted = () => {
    setDocumentRefresh(prev => prev + 1);
  };

  const loadScorecards = async () => {
    try {
      const token = getAuthToken();
      const response = await apiService.getAllScorecards(token, null); // null for global scorecards
      setScorecards(response.data || []);
    } catch (error) {
      console.log('No scorecards found or error loading:', error);
      setScorecards([]);
    }
  };



  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingOutlined style={{ color: '#8b5cf6' }} />
          <span>Global Settings</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={null}
      wrapClassName="global-settings-modal"
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'openai',
            label: (
              <span>
                <KeyOutlined />
                OpenAI Settings
                {openaiSettings && <Badge dot style={{ marginLeft: 8 }} />}
              </span>
            ),
            children: (
              <div className="openai-section">
                <div className="section-header">
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <KeyOutlined className="section-icon" />
                    <div>
                      <h3 className="section-title">OpenAI Configuration</h3>
                      <p className="section-description">
                        Configure your OpenAI integration settings including API access and vector storage options.
                      </p>
                    </div>
                  </div>
                </div>

                <Form
                  form={openaiForm}
                  layout="vertical"
                  onFinish={saveOpenaiSettings}
                  className="openai-form"
                >
                  <Form.Item
                    name="storage_type"
                    label="Storage Type"
                    rules={[{ required: true, message: 'Please select a storage type' }]}
                  >
                    <Radio.Group>
                      <Radio.Button value="openai_direct">
                        <DatabaseOutlined /> OpenAI Direct
                      </Radio.Button>
                      <Radio.Button value="openai_local_storage">
                        <DatabaseOutlined /> OpenAI with Local Storage
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>

                  <Alert
                    message="Storage Type Information"
                    description={
                      <div>
                        <Text strong>OpenAI Direct:</Text> Uses OpenAI's cloud-based vector storage and processing.<br/>
                        <Text strong>OpenAI with Local Storage:</Text> Uses local vector storage with OpenAI's processing capabilities.
                      </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <Form.Item
                    name="api_key"
                    label="OpenAI API Key"
                    rules={[
                      { required: true, message: 'Please enter your OpenAI API key' }
                      
                    ]}
                  >
                    <Input.Password
                      placeholder="sk-..."
                      visibilityToggle
                    />
                  </Form.Item>

                  <Form.Item
                    name="vector_storage_id"
                    label="Vector Storage ID"
                    help="OpenAI Vector Store ID (optional - leave empty to create new vector store)"
                    rules={[
                      { required: true, message: 'Please enter your OpenAI Vector Storage ID' }
                      
                    ]}
                  >
                    <Input
                      placeholder="vs_xxx..."
                      prefix={<DatabaseOutlined />}
                    />
                  </Form.Item>

                  <Form.Item
                    name="chat_prompt_id"
                    label="Chat Prompt ID"
                    help="Custom prompt ID for chat interactions (optional)"
                  >
                    <Input
                      placeholder="prompt_xxx..."
                      prefix={<SettingOutlined />}
                    />
                  </Form.Item>

                  <Form.Item
                    name="upload_prompt_id"
                    label="Compliance Prompt ID"
                    help="Custom prompt ID for file upload processing (optional)"
                    rules={[
                      { required: true, message: 'Please enter your Compliance Prompt ID' }
                    ]}
                  >
                    <Input
                      placeholder="prompt_xxx..."
                      prefix={<UploadOutlined />}
                    />
                  </Form.Item>

                  <Form.Item
                    name="apps_per_chunk"
                    label="Apps per Chunk"
                    help="Number of apps to process in each chunk (default: 10, range: 1-100)"
                    
                  >
                    <Input
                      type="number"
                      placeholder="10"
                      min={1}
                      max={100}
                      prefix={<BarChartOutlined />}
                    />
                  </Form.Item>

                  <Space>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      icon={<SaveOutlined />}
                    >
                      Save OpenAI Settings
                    </Button>
                  </Space>
                </Form>

                <Divider />

                <div style={{ marginTop: 24 }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>
                    Current Configuration
                  </h4>
                  <Card size="small">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong>🔐 OpenAI Integration</strong>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                          Storage: {openaiSettings?.storage_type || 'Not configured'}
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                          API Key: {openaiSettings?.api_key ? '••••••••••••' : 'Not set'}
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                          Vector Storage: {openaiSettings?.vector_storage_id || 'Auto-create'}
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                          Chat Prompt: {openaiSettings?.chat_prompt_id || 'Default'}
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                          Upload Prompt: {openaiSettings?.upload_prompt_id || 'Default'}
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                          Apps per Chunk: {openaiSettings?.apps_per_chunk || 10}
                        </div>
                      </div>
                      <div>
                        {openaiSettings ? (
                          <Badge status="success" text="Configured" />
                        ) : (
                          <Badge status="default" text="Not configured" />
                        )}
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            )
          },
          {
            key: 'instructions',
            label: (
              <span>
                <SettingOutlined />
                AI Instructions
                {(chatInstructions || uploadInstructions) && <Badge count={[chatInstructions, uploadInstructions].filter(Boolean).length} size="small" style={{ marginLeft: 8 }} />}
              </span>
            ),
            children: (
              <div className="instructions-section">
                <div className="section-header">
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <SettingOutlined className="section-icon" />
                    <div>
                      <h3 className="section-title">Global AI Instructions</h3>
                      <p className="section-description">
                        Set personalized instructions that will be applied to all AI interactions across all projects. 
                        Configure different instructions for chat and upload scenarios.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Instruction Type Selector */}
                <div className="instruction-type-selector" style={{ marginBottom: 24 }}>
                  <Tabs
                    activeKey={activeInstructionType}
                    onChange={setActiveInstructionType}
                    items={[
                      {
                        key: 'chat',
                        label: (
                          <span>
                            💬 Chat Instructions
                            {chatInstructions && <Badge dot style={{ marginLeft: 8 }} />}
                          </span>
                        ),
                      },
                      {
                        key: 'upload',
                        label: (
                          <span>
                            📁 Upload Instructions
                            {uploadInstructions && <Badge dot style={{ marginLeft: 8 }} />}
                          </span>
                        ),
                      }
                    ]}
                  />
                </div>

                {/* Current Instruction Type Info */}
                <Alert
                  message={
                    activeInstructionType === 'chat' 
                      ? "Chat Instructions" 
                      : "Upload Instructions"
                  }
                  description={
                    activeInstructionType === 'chat'
                      ? "These instructions will be used when the AI responds to chat messages and conversations."
                      : "These instructions will be used when the AI processes uploaded files and documents."
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={saveInstructions}
                  className="instructions-form"
                >
                  <Form.Item
                    name="instructions"
                    label={
                      <span className="form-label">
                        {activeInstructionType === 'chat' ? 'Chat' : 'Upload'} Instructions
                      </span>
                    }
                    help={
                      activeInstructionType === 'chat'
                        ? "These instructions will be applied to all AI chat interactions unless overridden by project-specific instructions"
                        : "These instructions will be applied when AI processes uploaded files unless overridden by project-specific instructions"
                    }
                  >
                    <TextArea
                      rows={8}
                      placeholder={
                        activeInstructionType === 'chat'
                          ? "e.g., Always provide step-by-step compliance analysis with risk assessment levels (Low, Medium, High). Include specific regulatory references when possible."
                          : "e.g., When processing uploaded documents, focus on extracting key compliance issues, categorize findings by severity, and provide actionable recommendations."
                      }
                    />
                  </Form.Item>

                  <Space>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      icon={<SaveOutlined />}
                    >
                      Save {activeInstructionType === 'chat' ? 'Chat' : 'Upload'} Instructions
                    </Button>
                    
                    {(activeInstructionType === 'chat' ? chatInstructions : uploadInstructions) && (
                      <Popconfirm
                        title={`Delete ${activeInstructionType === 'chat' ? 'Chat' : 'Upload'} Instructions`}
                        description={`Are you sure you want to delete your global ${activeInstructionType} instructions?`}
                        onConfirm={() => deleteInstructions((activeInstructionType === 'chat' ? chatInstructions : uploadInstructions).id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                        >
                          Delete {activeInstructionType === 'chat' ? 'Chat' : 'Upload'} Instructions
                        </Button>
                      </Popconfirm>
                    )}
                  </Space>
                </Form>

                <Divider />

                <div style={{ marginTop: 24 }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>
                    Instruction Summary
                  </h4>
                  <div className="instructions-summary">
                    <Card size="small" style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong>💬 Chat Instructions</strong>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                            Used for AI chat interactions
                          </div>
                          {chatInstructions && (
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: 4 }}>
                              {chatInstructions.instructions.substring(0, 80)}...
                            </div>
                          )}
                        </div>
                        <div>
                          {chatInstructions ? (
                            <Badge status="success" text="Configured" />
                          ) : (
                            <Badge status="default" text="Not configured" />
                          )}
                        </div>
                      </div>
                    </Card>
                    
                    <Card size="small">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong>📁 Upload Instructions</strong>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                            Used for file processing
                          </div>
                          {uploadInstructions && (
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: 4 }}>
                              {uploadInstructions.instructions.substring(0, 80)}...
                            </div>
                          )}
                        </div>
                        <div>
                          {uploadInstructions ? (
                            <Badge status="success" text="Configured" />
                          ) : (
                            <Badge status="default" text="Not configured" />
                          )}
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            )
          },
          {
            key: 'scorecards',
            label: (
              <span>
                <BarChartOutlined />
                Scorecards
                {scorecards.length > 0 && <Badge count={scorecards.length} size="small" style={{ marginLeft: 8 }} />}
              </span>
            ),
            children: (
              <div className="scorecards-section">
                <ScorecardManager 
                  projectId={null} // null for global scorecards
                />
              </div>
            )
          },
          {
            key: 'documents',
            label: (
              <span>
                <FileOutlined />
                Vector Documents
                {documents.length > 0 && <Badge count={documents.length} size="small" style={{ marginLeft: 8 }} />}
              </span>
            ),
            children: (
              <div className="documents-section">
                <div className="section-header">
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <FileOutlined className="section-icon" />
                    <div>
                      <h3 className="section-title">Document Management</h3>
                      <p className="section-description">
                        Upload and organize documents by type. These documents will be automatically 
                        available as context in all AI conversations, providing more accurate and relevant responses.
                      </p>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <DocumentUpload
                    projectId={null} // Global documents don't have project ID
                    onUploadSuccess={handleDocumentUploadSuccess}
                    onUploadError={handleDocumentUploadError}
                  />
                </div>

                <DocumentList
                  projectId={null} // Global documents don't have project ID
                  refresh={documentRefresh}
                  onDocumentDeleted={handleDocumentDeleted}
                />
              </div>
            )
          }
        ]}
      />
    </Modal>
  );
};

export default GlobalSettingsModal;